import { DateTime } from 'luxon';
import type { DrillData } from '@/components/forms/form_builder/FormFieldConfigHelper';
import type { BlobModel } from '@/models/blob';
import { OTHER_CHOICE_CODE } from '@/models/entry';
import { isClientOnly } from '@/models/fieldAnswer';
import {
  DateMode,
  type FormField,
  type FormFieldChoice,
  type FormFieldType,
  type MatrixColumn,
  type MatrixColumnType,
  type MatrixTableRow
} from '@/models/form';
import type { FileSlot } from './files/fileUploads';
import { isCalculatedField } from './effects';
import { readRangeAnswer } from './items/numberRangeAnswer';

/**
 * What the conflict screen lists when a save is refused because someone saved the entry after the
 * reader opened it (ENG-9078, ENG-9073 decision 6). The reload replaces the form with the server's
 * version, so the list is read from the reader's form before it goes, for them to re-enter by hand.
 * Nothing here writes an answer back: a merge would put answers into an audited record the reader
 * never saw.
 *
 * Free of Vue and the store, like `entryScreen.ts`, so `__tests__/saveConflict.test.ts` can walk
 * every field type.
 */

/** One field the reader had changed, and what they had put in it as one line: `''` for an answer they cleared. */
export interface ReaderChange {
  identifier: string;
  label: string;
  value: string;
}

/** A save the API refused on its version check. `changes` is empty when the reader had changed nothing it lists. */
export interface SaveConflict {
  changes: ReaderChange[];
}

/** The entry's two per-entry sets (ADR 0011), as the store holds them. */
export interface ChangeSets {
  changed: ReadonlySet<string>;
  touched: ReadonlySet<string>;
}

/** The files a file field's or matrix file cell's slot holds, which the answer names only by id. */
export type EntryFiles = (slot: FileSlot) => BlobModel[];

const NO_FILES: EntryFiles = () => [];

/**
 * Whether a Calculated field's value is one the reader typed. Its calculation writes it into the
 * changed set on its own, so only a form that lets the reader override it can hold a value they
 * typed, and only once they touched it.
 *
 * Touched also takes in a required field Save counted while it was empty, so on an overridable field
 * a value the calculation wrote after that Save is listed too. That errs towards a line too many: the
 * reader re-enters what the calculation reads and it runs again, where a missed override is an
 * answer lost.
 */
const typedIntoCalculated = (field: FormField, touched: ReadonlySet<string>) =>
  field.options.overrideCalculation === true && touched.has(field.identifier);

/**
 * The fields the reader changed, in the form's order: the changed set, less the fields the entry
 * adds for itself, archived fields, and a Calculated field's value its calculation wrote. A field
 * Save only counted is touched and not changed, so it never reaches the list.
 */
export const readerChanges = (fields: FormField[], { changed, touched }: ChangeSets, files: EntryFiles = NO_FILES): ReaderChange[] =>
  fields
    .filter(field => !isClientOnly(field) && !field.archivedAt && changed.has(field.identifier))
    .filter(field => !isCalculatedField(field) || typedIntoCalculated(field, touched))
    .map(field => ({ identifier: field.identifier, label: field.label, value: answerText(field, files) }));

const stringOf = (value: unknown) => value === undefined || value === null ? '' : `${value}`;

const isSingleChoice = (field: Pick<FormField, 'options'>) => field.options.maximumChoicesCount == 1;

/** What the field's control left in `value`, for the types whose answer is that string. */
const valueText = (field: FormField) => stringOf(field.value);

const numberText = (value: number | null | undefined) => Number.isNaN(value) ? '' : stringOf(value);

const rangeText = (answer: string | undefined) => {
  const { from, to } = readRangeAnswer(answer);
  if (from === undefined && to === undefined) {
    return '';
  }
  return `${stringOf(from)} – ${stringOf(to)}`;
};

const labelsOf = (choices: FormFieldChoice[] | FormFieldChoice | undefined) =>
  (Array.isArray(choices) ? choices : choices ? [choices] : []).map(choice => choice.label).join(', ');

const choicesText = (field: FormField) => {
  if (isSingleChoice(field)) {
    const picked = field.choiceValue as FormFieldChoice | undefined;
    if (!picked?.value && field.options.allowOther && field.otherChoiceValue?.value === OTHER_CHOICE_CODE) {
      return stringOf(field.otherChoiceValue.otherValue);
    }
    return stringOf(picked?.label);
  }
  const labels = (field.options.choices ?? []).filter(choice => choice.checked).map(choice => choice.label);
  if (field.options.allowOther && field.otherChoiceValue?.checked) {
    labels.push(stringOf(field.otherChoiceValue.otherValue));
  }
  return labels.join(', ');
};

/**
 * In the format the field or column is configured with, as its date box draws it, with the box's
 * defaults where the form sets none.
 */
const dateText = (date: Date | undefined, { mode, format, timeFormat }: { mode?: string; format?: string; timeFormat?: number }) => {
  if (!date) {
    return '';
  }
  const time = timeFormat == 12 ? 'hh:mm a' : 'HH:mm';
  const day = format || 'yyyy-MM-dd';
  const pattern = mode === DateMode.TIME ? time : mode === DateMode.DATE ? day : `${day} ${time}`;
  return DateTime.fromJSDate(date).toFormat(pattern);
};

const locationText = (field: FormField) => {
  if (!field.locationValue) {
    return valueText(field);
  }
  const { latitude, longitude } = field.locationValue.coords;
  return `${latitude}, ${longitude}`;
};

const unitText = (field: FormField) => {
  const number = numberText(field.numberValue);
  if (number === '') {
    return '';
  }
  const inequality = (field.choiceValue as FormFieldChoice | undefined)?.label;
  return [inequality, number, field.value].filter(part => part !== undefined && part !== '').join(' ');
};

const drilldownText = (field: FormField) =>
  ((field.drillValue as DrillData[] | undefined) ?? []).map(level => level.label).join(' › ');

/** The names of the files `ids` names, from the store's list, or the id where the list has no name for it. */
const fileNames = (ids: string[] | undefined, files: BlobModel[] = []) =>
  (ids ?? []).map(id => files.find(file => file.identifier === id)?.name ?? id).join(', ');

/** Who the reader put on the signature: a drawn signature has no line of text, but its signatories do. */
const signatoriesText = (field: FormField) =>
  (field.signatureValue?.signatories ?? [])
    .filter(signatory => !signatory.removed)
    .map(signatory => signatory.signatory || signatory.email)
    .join(', ');

/**
 * A matrix cell as one value. A ticked choice cell is its column's own label, since the tick is the
 * answer, and an unticked one says nothing.
 */
const CELL_TEXT: Record<MatrixColumnType, (cell: MatrixColumn, files: BlobModel[]) => string> = {
  Text: cell => stringOf(cell.textValue),
  Number: cell => cell.options?.range ? rangeText(cell.textValue) : numberText(cell.numberValue),
  RadioButton: cell => cell.booleanValue ? cell.label : '',
  Checkbox: cell => cell.booleanValue ? cell.label : '',
  Date: cell => dateText(cell.dateValue, cell.options ?? {}),
  Dropdown: cell => labelsOf(cell.choiceValue),
  File: (cell, files) => fileNames(cell.fileIds, files)
};

/** Each row with an answer, as its header then its answered cells: "Row 1: Dose 4, Route Oral". */
const matrixText = (field: FormField, files: EntryFiles) =>
  (field.matrixTableRowValues ?? [])
    .map((row: MatrixTableRow) => {
      const cells = (row.columnValues ?? [])
        .map(cell => {
          const value = (CELL_TEXT[cell.type] ?? CELL_TEXT.Text)(
            cell, files({ fieldId: field.identifier, rowId: row.rowId, columnId: `${cell.id}` }));
          if (value === '') {
            return '';
          }
          return value === cell.label ? value : `${cell.label} ${value}`;
        })
        .filter(cell => cell !== '');
      return cells.length > 0 ? `${row.rowHeader}: ${cells.join(', ')}` : '';
    })
    .filter(row => row !== '')
    .join('; ');

/* Keyed by the enum's string values, as `fieldAnswer.ts`'s table is and for the same reason. */
const ANSWER_TEXT: Record<FormFieldType, (field: FormField, files: EntryFiles) => string> = {
  Text: valueText,
  Number: field => field.options.range ? rangeText(field.value) : numberText(field.numberValue),
  Slider: field => numberText(field.numberValue),
  Choice: choicesText,
  Dropdown: field => isSingleChoice(field) ?
    stringOf((field.choiceValue as FormFieldChoice | undefined)?.label) :
    labelsOf(field.choiceValue),
  Date: field => field.dateValue ? dateText(field.dateValue, field.options) : valueText(field),
  Location: locationText,
  File: (field, files) => fileNames(field.fileIds, files({ fieldId: field.identifier })),
  Signature: signatoriesText,
  Matrix: matrixText,
  Drilldown: drilldownText,
  Unit: unitText,
  Barcode: valueText,
  Title: valueText,
  Section: valueText,
  // The entry's own fields, which `readerChanges` never lists.
  Branding: () => '',
  SubmitButton: () => ''
};

/** What `field` holds, as one line a reader can type back in. `files` is the store's lists, for the file names. */
export const answerText = (field: FormField, files: EntryFiles = NO_FILES): string => ANSWER_TEXT[field.type](field, files);
